import appService from '@/modules/app/services/appService';
import embeddedAppStore from '@/modules/app/store/embeddedAppStore';

const state = {
  billingApp: undefined,
  availableMarketApps: [],
  externalApplications: [],
};

/**
 * Only these apps will use the angular implementation of embedded apps
 */
const ANGULAR_EMBEDDED_APPS = [
  'emailsignature',
  'vcitapay',
  'appadvancecampaigneditor',
  'yextApp',
  'Quickbooks',
  'tsireports',
  'anapp12',
  'appfacebook',
  'appquickbooks',
  'appfullcontact',
  'yextAppInt',
  'thryvpaybeta_556',
  'signaturesprod',
  'ThryvPay',
  'socialOverviewLabs',
  'signaturesemp',
  'emailsignatureqa',
  'businesswebsiteeditor_775',
  'seolistings_776',
  'businesswebsiteeditor_763',
  'seolistings_764',
  'submitasupportrequest',
  'openidapptest',
  'businesswebsiteeditoro',
  'dudatest',
  'mywikipages',
  'mastercarddemo',
  'roojoom',
  'analyticstest',
  'demoteamchat',
  'clientsimportplayground',
  'telcodemo',
  'qrcode',
  'teamchat_554',
  'instagram_818',
  'mySubscriptionsSandBox',
  'testPremissions',
  'uberall',
  'siteemono',
  'monowebbuilder',
  'websitebuilderfree',
  'yolaintstaging',
  'websitebuilderpro',
  'websitebuilderprod',
  'websitebuilderote',
  'yolawebsitebuilder',
  'websitebuilderpie',
  'webexDemoEnvApp',
  'demoteamchat_601',
  'gmailaddonintegration',
  'cashflowqa',
  'cashflowscalestage_869',
  'cashflowscalestage',
  'cashflowscaledev_824',
  'cashflowforgestage_813',
  'authnz360dev',
  'nucleus',
  'smenucleus_646',
  'yextDemoApp',
];

// TODO: this data should be moved to the manifest once it's implemented in the backend
const specificAppDataOverride = {
  // To remove the header for a specific app
  // [app_code_name]: {
  //   show_header: false,
  // },
};

/**
 * Mark specific apps that were migrated to POV
 * @param {Object} apps - Object containing app data
 * @param {Bool} forceMigrated - Force marking as migrated app
 * @returns {Array} Transformed array of apps
 */
const markMigratedApps = (apps, forceMigrated) => [...Object.values(apps).map((item) => {
  if (forceMigrated || (item.app_code_name && !ANGULAR_EMBEDDED_APPS.includes(item.app_code_name))) {
    return {
      ...item,
      migrated: true,
      show_header: true,
      ...specificAppDataOverride[item.app_code_name],
    };
  }
  return item;
})];

const mutations = {
  // TODO: is there a duplication of data here? can we normalize it so that a billing app is just a getter?
  setBillingApp(state, app) {
    state.billingApp = app;
  },
  setAvailableMarketApps(state, apps) {
    state.availableMarketApps = apps;
  },
  setExternalApplications(state, apps) {
    state.externalApplications = apps;
  },
};

const actions = {
  async loadBillingAppData({ commit, state }, businessUid) {
    if (!state.billingApp && !state.billingApp?.uid) {
      const result = await appService.getAppsByType({ type: 'billing_app', businessUid });
      if (result.code !== 'error') {
        commit('setBillingApp', result.apps[0]);
      }
    }
  },
  async getAvailableMarketApps({ commit, state, rootGetters }, force = false) {
    if (!state.availableMarketApps.length || force) {
      try {
        const result = await appService.getAvailableMarketApps();
        const filteredApps = markMigratedApps(result.apps, rootGetters['BusinessStore/hasFeature']('force_embedded_apps_in_POV'));
        commit('setAvailableMarketApps', filteredApps);
      } catch (error) {
        console.error('Failed to call getAvailableMarketApps', error);
      }
    }
  },
  async getExternalApplications({ commit, state, rootGetters }, force = false) {
    if (!state.externalApplications.length || force) {
      try {
        const result = await appService.getExternalApps();
        const filteredApps = markMigratedApps(result.apps, rootGetters['BusinessStore/hasFeature']('force_embedded_apps_in_POV'));
        commit('setExternalApplications', filteredApps);
      } catch (error) {
        console.error('Failed to call getExternalApps', error);
      }
    }
  },
};

const getters = {
  billingApp: (state) => state.billingApp,
  availableMarketApps: (state) => state.availableMarketApps,
  getMarketAppById: (state) => (id) => state.availableMarketApps.find((app) => app.app_code_name === id),
  externalApplications: (state) => state.externalApplications,
  getExternalAppById: (state) => (id) => state.externalApplications.find((app) => app.app_code_name === id),
};

const modules = {
  EmbeddedAppStore: embeddedAppStore,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
  modules,
};
